import React from "react"
import { Link, useNavigate } from "react-router-dom"
import type { TFunction } from "i18next"
import { Routes } from "../routes"
import "./header-redesign.css"
import { formatNumber } from "../utils/formatDate"
import { performAppLogout } from "../utils/performAppLogout"
import { UI_BUTTON_EVENTS } from "../services/api/uiButtonStatsService"

function initialsFromName(name: string | undefined): string {
    if (!name?.trim()) return "?"
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export interface UserHeaderMenuProps {
    t: TFunction
    loadcompany: boolean
    fullname: string
    email: string
    role: string | undefined
    planLabel: string
    creditsAvailable: number | null
    creditsLoading: boolean
    isAdmin: boolean
    /** Masquer « changer de société » (ex. dashboard réponses collecte). Défaut : true. */
    showChangeCompanyInMenu?: boolean
    /** Compteurs accueil (vue d’ensemble) : voir `HeaderLayout` / `home.topbar.*` */
    trackHomeTopbar?: (key: string) => void | Promise<void>
}

const UserHeaderMenu = (props: UserHeaderMenuProps) => {
    const {
        t,
        loadcompany,
        fullname,
        email,
        role,
        planLabel,
        creditsAvailable,
        creditsLoading,
        isAdmin,
        showChangeCompanyInMenu = true,
        trackHomeTopbar
    } = props

    const navigate = useNavigate()

    const roleBadge =
        role === "ADMIN"
            ? t("layout.header.roleAdmin")
            : role
              ? t("layout.header.roleUser")
              : ""

    return (
        <div className="dropdown dropstart hdr-user-menu-wrap">
            <button
                type="button"
                className="dropdown-toggle hdr-user-trigger hdr-user-dd"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                id="hdrUserMenuButton"
                onClick={() => {
                    void trackHomeTopbar?.(UI_BUTTON_EVENTS.home.topbarUserMenu)
                }}
            >
                <div className="hdr-avatar">{initialsFromName(fullname)}</div>
                <div>
                    <div className="hdr-user-name">{fullname}</div>
                    <div className="hdr-user-plan">
                        {planLabel}
                        {creditsAvailable !== null && (
                            <>
                                {" · "}
                                {isAdmin ? (
                                    <span
                                        role="presentation"
                                        className="hdr-user-plan-link"
                                        title={t("dashboard.availableCredits")}
                                        onClick={async (e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            await trackHomeTopbar?.(
                                                UI_BUTTON_EVENTS.home.topbarUserMenuCredits
                                            )
                                            navigate(Routes.DASHBOARDS)
                                        }}
                                    >
                                        {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                        {t("dashboard.totalCredits")}
                                    </span>
                                ) : (
                                    <>
                                        {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                        {t("dashboard.totalCredits")}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <span className="hdr-user-chevron" aria-hidden>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M4 6L8 10L12 6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </button>

            <div
                className="dropdown-menu hdr-user-menu-panel"
                aria-labelledby="hdrUserMenuButton"
            >
                <div className="hdr-user-menu-profile">
                    <div className="hdr-user-menu-avatar">{initialsFromName(fullname)}</div>
                    <div className="hdr-user-menu-profile-text">
                        <div className="hdr-user-menu-profile-name">{fullname}</div>
                        <div className="hdr-user-menu-profile-email">{email}</div>
                        {roleBadge ? (
                            <span className="hdr-user-menu-role">
                                <i className="fa-solid fa-shield-halved hdr-user-menu-role-icon" aria-hidden />
                                {roleBadge}
                            </span>
                        ) : null}
                        {creditsAvailable !== null && (
                            <div className="hdr-user-menu-profile-meta">
                                {planLabel}
                                {" · "}
                                {isAdmin ? (
                                    <Link
                                        className="hdr-user-menu-meta-link"
                                        to={Routes.DASHBOARDS}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            void trackHomeTopbar?.(
                                                UI_BUTTON_EVENTS.home.topbarUserMenuCredits
                                            )
                                        }}
                                    >
                                        {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                        {t("dashboard.totalCredits")}
                                    </Link>
                                ) : (
                                    <>
                                        {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                        {t("dashboard.totalCredits")}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="hdr-user-menu-divider" role="separator" />

                <nav className="hdr-user-menu-nav" aria-label={t("layout.header.userMenuNav")}>
                    <a
                        className="hdr-user-menu-item"
                        href="#/"
                        data-bs-toggle="modal"
                        data-bs-target="#inviteOwner"
                        onClick={(e) => {
                            e.stopPropagation()
                            void trackHomeTopbar?.(
                                UI_BUTTON_EVENTS.home.topbarUserMenuInviteOwner
                            )
                        }}
                    >
                        <i className="fa-solid fa-user-plus hdr-user-menu-item-icon" aria-hidden />
                        <span>{t("navigation.inviteOwner")}</span>
                    </a>
                    {loadcompany && (
                        <a
                            className="hdr-user-menu-item"
                            href="#/"
                            data-bs-toggle="modal"
                            data-bs-target="#requestApproval"
                            onClick={(e) => {
                                e.stopPropagation()
                                void trackHomeTopbar?.(
                                    UI_BUTTON_EVENTS.home.topbarUserMenuApprovalRequests
                                )
                            }}
                        >
                            <i className="fa-solid fa-clipboard-check hdr-user-menu-item-icon" aria-hidden />
                            <span>{t("layout.header.approvalRequests")}</span>
                        </a>
                    )}
                    {loadcompany && showChangeCompanyInMenu && (
                        <a
                            className="hdr-user-menu-item"
                            href="#/"
                            data-bs-toggle="modal"
                            data-bs-target="#changeCompany"
                            onClick={(e) => {
                                e.stopPropagation()
                                void trackHomeTopbar?.(
                                    UI_BUTTON_EVENTS.home.topbarUserMenuChangeCompany
                                )
                            }}
                        >
                            <i className="fa-solid fa-building hdr-user-menu-item-icon" aria-hidden />
                            <span>{t("layout.header.company")}</span>
                        </a>
                    )}
                    {isAdmin && (
                        <Link
                            className="hdr-user-menu-item"
                            to={Routes.DASHBOARDS}
                            onClick={(e) => {
                                e.stopPropagation()
                                void trackHomeTopbar?.(
                                    UI_BUTTON_EVENTS.home.topbarUserMenuDashboard
                                )
                            }}
                        >
                            <i className="fa-solid fa-chart-line hdr-user-menu-item-icon" aria-hidden />
                            <span>{t("dashboard.dashboard")}</span>
                        </Link>
                    )}
                </nav>

                <div className="hdr-user-menu-divider" role="separator" />

                <div className="hdr-user-menu-footer">
                    <button
                        type="button"
                        className="hdr-user-menu-logout"
                        onClick={() => {
                            void (async () => {
                                await trackHomeTopbar?.(
                                    UI_BUTTON_EVENTS.home.topbarUserMenuLogout
                                )
                                await performAppLogout()
                            })()
                        }}
                    >
                        <i className="fa-solid fa-arrow-right-from-bracket hdr-user-menu-item-icon" aria-hidden />
                        <span>{t("navigation.logOut")}</span>
                    </button>
                </div>
            </div>
        </div>
    )
}

export default UserHeaderMenu
